import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["betaDetails", "sourceId", "onInstallComplete", "onInstallError", "onClick"];
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { gate } from 'hub-http/gates';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useDetailsPageContext } from 'marketplace-ui-common/context/DetailsPageContext';
import { getWorkflowFlowUrl, navigateTo } from 'marketplace-ui-common/utils/DetailsPageUrls';
import { getUserHasEmbeddedAutomationAccess } from 'marketplace-ui-common/utils/scopes';
import { useCallback, useMemo, useRef, useState } from 'react';
import ToolAccessTooltip from 'ui-tool-access/ToolAccessTooltip';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import { useInstantiateTemplateMutation, useListInstantiationRequestsQuery } from '../../__generated__/chirp/com/hubspot/automation/templatesrpc/TemplateInstantiationService.hooks';
import { useGetActiveToolAccessRequestForScopeQuery } from '../../__generated__/chirp/com/hubspot/tool/access/rpc/ToolAccessService.hooks';
import { useAdditionalListingInstallability } from './hooks/useAdditionalListingInstallability';
import { useGetInstallability } from './hooks/useGetInstallability';
import { JoinPublicBetaModal } from './JoinPublicBetaModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MAX_POLLING_TIME = 40000;
const WORKFLOWS_PRO_PLUS_SCOPE_NAME = 'workflows-app-access'; // only available to pro+ portals
const WORKFLOWS_MINIMUM_SCOPE_NAME = 'workflows-edit-team-owned'; // available to some starter portals
const AGENT_HUB_GATE_NAME = 'Workflows:ModernEditor';
export function TemplateCTAButton(_ref) {
  var _userInfo$gates2;
  let {
      betaDetails,
      sourceId,
      onInstallComplete,
      onInstallError,
      onClick
    } = _ref,
    buttonProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    appContext,
    listingId,
    published,
    isEmbedded,
    name,
    productType,
    requiredForInstallListingIds,
    trackInteraction,
    trackInstall,
    userInfo
  } = useDetailsPageContext();
  const isPublicBeta = (betaDetails === null || betaDetails === void 0 ? void 0 : betaDetails.betaStage) === 'PUBLIC_BETA';
  const [isPublicBetaModalOpen, setIsPublicBetaModalOpen] = useState(false);
  const hasJoinedPublicBeta = useMemo(() => {
    if (isPublicBeta && betaDetails !== null && betaDetails !== void 0 && betaDetails.gateName) {
      var _userInfo$gates;
      // eslint-disable-next-line hubspot-dev/no-bare-gate-strings
      return userInfo === null || userInfo === void 0 || (_userInfo$gates = userInfo.gates) === null || _userInfo$gates === void 0 ? void 0 : _userInfo$gates.includes(gate(betaDetails.gateName));
    }
    return true;
  }, [isPublicBeta, betaDetails === null || betaDetails === void 0 ? void 0 : betaDetails.gateName, userInfo === null || userInfo === void 0 ? void 0 : userInfo.gates]);
  const isUngatedForUnifiedBuilder = userInfo === null || userInfo === void 0 || (_userInfo$gates2 = userInfo.gates) === null || _userInfo$gates2 === void 0 ? void 0 : _userInfo$gates2.includes(
  // eslint-disable-next-line hubspot-dev/no-bare-gate-strings
  gate(AGENT_HUB_GATE_NAME));
  const hasEmbeddedAutomationAccess = getUserHasEmbeddedAutomationAccess(userInfo);
  const [pollingForCurrentTask, setPollingForCurrentTask] = useState(false);
  const maxPollingTimeReached = useRef(false);
  const taskId = useRef(undefined);
  const {
    loading: installabilityLoading,
    portalInstallable,
    userInstallable,
    uninstallableReason,
    missingPlanTooltip,
    isOnlyMissingPublicBetaAccessForInstall
  } = useGetInstallability({
    listingId,
    appContext,
    published,
    productType
  });
  const {
    collectors,
    effectiveLoading,
    effectivePortalInstallable,
    effectiveUserInstallable,
    effectiveMissingPlanTooltip,
    requiredPublicBetaListings
  } = useAdditionalListingInstallability({
    requiredForInstallListingIds,
    published,
    appContext,
    productType,
    primaryLoading: installabilityLoading,
    primaryPortalInstallable: portalInstallable,
    primaryUserInstallable: userInstallable,
    primaryMissingPlanTooltip: missingPlanTooltip
  });
  const [openRequiredBetaSlug, setOpenRequiredBetaSlug] = useState(null);
  const openRequiredBetaListing = useMemo(() => requiredPublicBetaListings.find(b => b.slug === openRequiredBetaSlug), [requiredPublicBetaListings, openRequiredBetaSlug]);
  const needWorkflowMinimumAccessScope = useMemo(() => {
    var _uninstallableReason$;
    return portalInstallable && (uninstallableReason === null || uninstallableReason === void 0 || (_uninstallableReason$ = uninstallableReason.missingUserScopes) === null || _uninstallableReason$ === void 0 ? void 0 : _uninstallableReason$.includes(WORKFLOWS_MINIMUM_SCOPE_NAME));
  }, [portalInstallable, uninstallableReason]);
  const needWorkflowProPlusScope = useMemo(() => {
    var _uninstallableReason$2;
    return portalInstallable && (uninstallableReason === null || uninstallableReason === void 0 || (_uninstallableReason$2 = uninstallableReason.missingUserScopes) === null || _uninstallableReason$2 === void 0 ? void 0 : _uninstallableReason$2.includes(WORKFLOWS_PRO_PLUS_SCOPE_NAME));
  }, [portalInstallable, uninstallableReason]);
  const workflowScopeForToolAccessRequest = needWorkflowMinimumAccessScope ? WORKFLOWS_MINIMUM_SCOPE_NAME : WORKFLOWS_PRO_PLUS_SCOPE_NAME;
  const {
    data: workflowsAccessRequestData,
    refetch: refetchWorkflowsAccessRequest
  } = useGetActiveToolAccessRequestForScopeQuery({
    variables: {
      scopeName: workflowScopeForToolAccessRequest
    },
    skip: appContext === 'admin-preview' || !needWorkflowMinimumAccessScope && !needWorkflowProPlusScope
  });
  const onToolAccessRequestComplete = useCallback(() => {
    trackInteraction({
      action: 'workflows app access request sent'
    });
    void refetchWorkflowsAccessRequest().catch(error => {
      console.error('Error refetching workflows app access request status:', error);
    });
  }, [refetchWorkflowsAccessRequest, trackInteraction]);
  const sourceIdAsNumber = typeof sourceId === 'number' ? sourceId : parseInt(sourceId, 10);
  const [instantiateTemplate, {
    loading: instantiateTemplateLoading
  }] = useInstantiateTemplateMutation();
  const {
    refetch: refetchTemplateInstances
  } = useListInstantiationRequestsQuery({
    variables: {
      templateId: sourceIdAsNumber
    },
    skip: appContext !== 'app'
  });
  function showGenericErrorAlert() {
    FloatingAlertStore.addAlert({
      type: 'danger',
      maxInlineSize: 'unset',
      sticky: true,
      titleText: I18n.text('detailsPage.productDetailsCard.alerts.templates.templateInstantiationFailedAlertTitle'),
      message: I18n.text('detailsPage.productDetailsCard.alerts.templates.templateInstantiationFailedAlertMessage')
    });
    if (onInstallError) {
      onInstallError();
    }
  }
  function pollForCompletedTask() {
    setPollingForCurrentTask(true);
    refetchTemplateInstances().then(response => {
      var _response$data;
      const currentTask = response === null || response === void 0 || (_response$data = response.data) === null || _response$data === void 0 || (_response$data = _response$data.result) === null || _response$data === void 0 || (_response$data = _response$data.results) === null || _response$data === void 0 ? void 0 : _response$data.findLast(task => task.taskId === taskId.current);
      if (currentTask && currentTask.status === 'COMPLETED') {
        if (currentTask.flowId) {
          var _currentTask$viewLoca;
          trackInteraction({
            action: 'created workflow from template',
            sourceId: [`WORKFLOW_TEMPLATE__${sourceIdAsNumber}`],
            value: String(currentTask.flowId)
          });
          navigateTo(!hasEmbeddedAutomationAccess && ((_currentTask$viewLoca = currentTask.viewLocation) === null || _currentTask$viewLoca === void 0 ? void 0 : _currentTask$viewLoca.url) || getWorkflowFlowUrl(currentTask.flowId, isUngatedForUnifiedBuilder, currentTask.targetType, hasEmbeddedAutomationAccess), isEmbedded);
          if (onInstallComplete) {
            onInstallComplete();
          }
        } else {
          console.error('Missing flow Id in completed task response');
          showGenericErrorAlert();
        }
        setPollingForCurrentTask(false);
      } else if (maxPollingTimeReached.current || currentTask && currentTask.status === 'FAILED') {
        showGenericErrorAlert();
        setPollingForCurrentTask(false);
      } else {
        setTimeout(() => {
          pollForCompletedTask();
        }, 2000);
      }
    }).catch(e => console.error(e));
  }
  function handleTemplateInstall() {
    trackInstall({});
    instantiateTemplate({
      variables: {
        targetType: 'INFER',
        templateId: sourceIdAsNumber
      }
    }).then(response => {
      var _response$data2;
      const createdTaskId = (_response$data2 = response.data) === null || _response$data2 === void 0 ? void 0 : _response$data2.result.taskId;
      if (createdTaskId) {
        taskId.current = createdTaskId;
        FloatingAlertStore.addAlert({
          type: 'success',
          maxInlineSize: 'unset',
          sticky: true,
          titleText: I18n.text('detailsPage.productDetailsCard.alerts.templates.templateInstantiationSuccessAlertTitle'),
          message: I18n.text('detailsPage.productDetailsCard.alerts.templates.templateInstantiationSuccessAlertMessage')
        });
      }
      setTimeout(() => {
        maxPollingTimeReached.current = true;
      }, MAX_POLLING_TIME);
      pollForCompletedTask();
    }).catch(e => {
      console.error(e);
      showGenericErrorAlert();
    });
  }
  function handlePostBetaJoin() {
    handleTemplateInstall();
  }
  let content = null;
  if (effectiveLoading || instantiateTemplateLoading || pollingForCurrentTask) {
    content = /*#__PURE__*/_jsx(LoadingButton, Object.assign({
      size: "medium",
      use: "primary",
      role: "button"
    }, buttonProps, {
      loading: true,
      disabled: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.install.WORKFLOW_TEMPLATE"
      })
    }));
  } else if (needWorkflowMinimumAccessScope || needWorkflowProPlusScope) {
    content = /*#__PURE__*/_jsx(ToolAccessTooltip, {
      scope: needWorkflowMinimumAccessScope ? WORKFLOWS_MINIMUM_SCOPE_NAME : WORKFLOWS_PRO_PLUS_SCOPE_NAME,
      onComplete: onToolAccessRequestComplete,
      children: /*#__PURE__*/_jsx(Button, Object.assign({
        use: "primary",
        "data-test-id": "disabled-install-button-with-tool-access-tooltip"
      }, buttonProps, {
        disabled: true,
        children: (workflowsAccessRequestData === null || workflowsAccessRequestData === void 0 ? void 0 : workflowsAccessRequestData.status) === 'ACTIVE' ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.uninstallableReasons.toolAccessRequestPending"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.install.WORKFLOW_TEMPLATE"
        })
      }))
    });
  } else if (isPublicBeta && !hasJoinedPublicBeta && isOnlyMissingPublicBetaAccessForInstall) {
    var _betaDetails$rolloutI;
    content = /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Button, Object.assign({
        use: "primary",
        onClick: () => {
          onClick === null || onClick === void 0 || onClick();
          trackInteraction({
            action: 'clicked join beta and use template cta button'
          });
          setIsPublicBetaModalOpen(true);
        }
      }, buttonProps, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.productDetailsCard.cta.joinBetaAndAdd.WORKFLOW_TEMPLATE`
        })
      })), isPublicBetaModalOpen && /*#__PURE__*/_jsx(JoinPublicBetaModal, {
        title: name,
        onClose: () => setIsPublicBetaModalOpen(false),
        rolloutId: (_betaDetails$rolloutI = betaDetails === null || betaDetails === void 0 ? void 0 : betaDetails.rolloutId) !== null && _betaDetails$rolloutI !== void 0 ? _betaDetails$rolloutI : 0,
        onSuccessfulJoin: handlePostBetaJoin,
        skipBetaAlert: false,
        agentHubBeta: (betaDetails === null || betaDetails === void 0 ? void 0 : betaDetails.gateName) === AGENT_HUB_GATE_NAME
      })]
    });
  } else if (!effectivePortalInstallable || !effectiveUserInstallable) {
    var _openRequiredBetaList;
    const uninstallableReasonTooltip = /*#__PURE__*/_jsx(_Fragment, {
      children: (requiredPublicBetaListings === null || requiredPublicBetaListings === void 0 ? void 0 : requiredPublicBetaListings.length) > 0 && effectiveMissingPlanTooltip == null ?
      /*#__PURE__*/
      // Show beta CTAs only if that's the only uninstallable reason
      _jsxs(_Fragment, {
        children: [I18n.text((requiredPublicBetaListings === null || requiredPublicBetaListings === void 0 ? void 0 : requiredPublicBetaListings.length) > 1 ? 'detailsPage.productDetailsCard.cta.uninstallableReasons.requiredListingInBeta.plural' : 'detailsPage.productDetailsCard.cta.uninstallableReasons.requiredListingInBeta.singular'), requiredPublicBetaListings.map(betaListing => /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(Button, {
            size: "sm",
            use: "transparent",
            onClick: () => setOpenRequiredBetaSlug(betaListing.slug),
            children: betaListing.betaDetails.gateName === AGENT_HUB_GATE_NAME ? I18n.text('detailsPage.productDetailsCard.cta.uninstallableReasons.joinAgentHubBeta') : /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.cta.uninstallableReasons.joinRequiredBeta",
              options: {
                name: betaListing.name
              }
            })
          })
        }, betaListing.slug))]
      }) : effectiveMissingPlanTooltip || I18n.text('detailsPage.productDetailsCard.cta.uninstallableReasons.cannotUseTemplate')
    });
    content = /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Tooltip, {
        textAlign: "start",
        title: uninstallableReasonTooltip,
        children: /*#__PURE__*/_jsx(Button, Object.assign({
          use: "primary"
        }, buttonProps, {
          disabled: true,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.productDetailsCard.cta.install.WORKFLOW_TEMPLATE"
          })
        }))
      }), openRequiredBetaListing && /*#__PURE__*/_jsx(JoinPublicBetaModal, {
        title: name,
        onClose: () => setOpenRequiredBetaSlug(null),
        rolloutId: (_openRequiredBetaList = openRequiredBetaListing.betaDetails.rolloutId) !== null && _openRequiredBetaList !== void 0 ? _openRequiredBetaList : 0,
        onSuccessfulJoin: () => {
          openRequiredBetaListing.refetchInstallability();
          setOpenRequiredBetaSlug(null);
        },
        skipBetaAlert: false,
        agentHubBeta: openRequiredBetaListing.betaDetails.gateName === AGENT_HUB_GATE_NAME
      })]
    });
  } else {
    content = /*#__PURE__*/_jsx(Button, Object.assign({
      use: "primary",
      onClick: handleTemplateInstall
    }, buttonProps, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.install.WORKFLOW_TEMPLATE"
      })
    }));
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [collectors, content]
  });
}